import { useAtom } from "jotai";
import toast from "react-hot-toast";
import axiosInstance from "helpers/api";
import { UserInfo } from "interfaces/user";
import Button from "components/base/Button";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Backdrop from "components/common/Backdrop";
import { user_info } from "jotai/UserJotaiInstance";
import { Card, Col, Form, Row, Spinner } from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import { addContactBreadcrumbItems } from "data/commonData";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface LeadType {
  id: number;
  lead_type: string;
}

interface LeadStatus {
  id: number;
  status: string;
}

interface Tag {
  tags: string;
}

type Inputs = {
  first_name: string;
  last_name: string;
  email: string;
  additional_email: string;
  phone: string;
  lead_source: string;
  city: string;
  state: string;
  street_name: string;
  zip_code: string;
  description: string;
  tags: Array<string>;
};

const AddLead = () => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Inputs>();

  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const [storeTags, setStoreTags] = useState<Tag[]>([]);
  const [tags, setTags] = useState<string>("");

  const [leadType, setLeadType] = useState<number | null>(null);
  const [leadStatus, setLeadStatus] = useState<number | null>(null);
  const [leadTypes, setLeadTypes] = useState<LeadType[]>([]);
  const [selectedLeadStatus, setSelectedLeadStatus] = useState<LeadStatus[]>([]);
  const [typeError, setTypeError] = useState(false);
  const [statusError, setStatusError] = useState(false);
  const [userInfo] = useAtom<UserInfo>(user_info);
  const [showAddLead, setShowAddLead] = useState(true);

  const fetchLeadTypes = async () => {
    try {
      const response = await axiosInstance.get("/leads/types");
      setLeadTypes(response.data.data.leadTypes);
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error && 
          error.response && typeof error.response === 'object' && 'data' in error.response &&
          error.response.data && typeof error.response.data === 'object' && 'message' in error.response.data) {
        toast.error(String(error.response.data.message));
      } else {
        toast.error("Failed to fetch lead types");
      }
    }
  };

  const matchStatus = async (id: number) => {
    setLeadType(id);

    try {
      const response = await axiosInstance.get(`/leads/status/${id}`);
      setSelectedLeadStatus(response.data.data.leadTypes);
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error && 
          error.response && typeof error.response === 'object' && 'data' in error.response &&
          error.response.data && typeof error.response.data === 'object' && 'message' in error.response.data) {
        toast.error(String(error.response.data.message));
      } else {
        toast.error("Failed to fetch status for this lead type");
      }
    }
  };

  const handleSelectStatus = (e: number) => {
    setLeadStatus(e);
  };

  useEffect(() => {
    fetchLeadTypes();
  }, []);

  const handleTags = () => {
    if (tags === "") {
      toast("Empty tag not allowed!");
      return;
    }
    setStoreTags([...storeTags, { tags: tags }]);
    toast.success("Tag Added");
    setTags("");
  };

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    if (!leadType) {
      setTypeError(true);
      return;
    }
    if (!leadStatus) {
      setStatusError(true);
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.post("/leads", {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        additional_email: data.additional_email,
        phone: data.phone,
        city: data.city,
        state: data.state,
        zip_code: data.zip_code,
        lead_source: data.lead_source,
        street_name: data.street_name,
        description: data.description,
        lead_types_id: leadType,
        lead_type_status_id: leadStatus,
        tags: storeTags,
      });
      toast.success(response.data.message);
      reset();
      setLoading(false);
      navigate("/leads");
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error && 
          error.response && typeof error.response === 'object' && 'data' in error.response &&
          error.response.data && typeof error.response.data === 'object' && 'message' in error.response.data) {
        toast.error(String(error.response.data.message));
      } else {
        toast.error("Failed to create lead");
      }
      setLoading(false);
    }
  };

  const handleDeleteTag = (tag: string) => {
    const filterTags = storeTags.filter((item: Tag) => item.tags !== tag);
    setStoreTags(filterTags);
  };

  const isAuthorized = userInfo?.payment_status === "Cancelled" ? false : true;

  useEffect(() => {
    if (!isAuthorized) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === "childList") {
            const backdropExists = document.getElementById("kairos-backdrop");
            if (!backdropExists) {
              setShowAddLead(false);
            }
          }
        });
      });
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
      return () => observer.disconnect();
    }
  }, [isAuthorized]);

  return (
    <div className="mb-9">
      {(isAuthorized || showAddLead) && (
        <>
          <PageBreadcrumb items={addContactBreadcrumbItems} />
          <div className="border-bottom border-translucent mb-7 mx-n3 px-2 mx-lg-n6 px-lg-6">
            <Row>
              <Col xl={9}>
                <div className="d-sm-flex justify-content-between">
                  <h2 className="mb-4">Create a new lead</h2>
                </div>
              </Col>
            </Row>
          </div>

          <Row>
            <Col xl={9} xxl={8}>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <div className="mb-6">
                  <h4 className="mb-3">Lead Information</h4>
                  <Row className="g-3">
                    <Col sm={6} md={4}>
                      <Form.Group>
                        <Form.Label>
                          First name
                          <span className="text-danger"> *</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="First name"
                          {...register("first_name", {
                            required: true,
                            maxLength: 100,
                          })}
                        />
                      </Form.Group>
                      {errors.first_name?.type === "required" && (
                        <p role="alert" className="text-danger fs-9 pt-1 mb-0">
                          First name is required
                        </p>
                      )}
                    </Col>
                    <Col sm={6} md={4}>
                      <Form.Group>
                        <Form.Label>Last name</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Last name"
                          {...register("last_name")}
                        />
                      </Form.Group>
                    </Col>

                    <Col sm={6} md={4}>
                      <Form.Group>
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          placeholder="Email"
                          {...register("email", { maxLength: 80 })}
                        />
                      </Form.Group>
                    </Col>
                    <Col sm={6} md={4}>
                      <Form.Group>
                        <Form.Label>Alternative email</Form.Label>
                        <Form.Control
                          type="email"
                          placeholder="Alternative email"
                          {...register("additional_email", { maxLength: 80 })}
                        />
                      </Form.Group>
                    </Col>
                    <Col sm={6} md={4}>
                      <Form.Group>
                        <Form.Label>
                          Phone <span className="text-danger"> *</span>
                        </Form.Label>
                        <Form.Control
                          type="tel"
                          inputMode="tel"
                          placeholder="Phone"
                          className="input-spin-none"
                          {...register("phone", {
                            required: true,
                            maxLength: 80,
                          })}
                        />
                      </Form.Group>

                      {errors.phone?.type === "required" && (
                        <p role="alert" className="text-danger fs-9 pt-1 mb-0">
                          Phone Number is required
                        </p>
                      )}
                    </Col>

                    <Col sm={6} md={4}>
                      <Form.Group>
                        <Form.Label>Source</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Source"
                          className="input-spin-none"
                          {...register("lead_source", { maxLength: 80 })}
                        />
                      </Form.Group>
                    </Col>

                    <Col sm={6} md={4}>
                      <Form.Group>
                        <Form.Label>
                          Lead Type
                          <span className="text-danger"> *</span>
                        </Form.Label>
                        <Form.Select
                          onChange={(e) => matchStatus(parseInt(e.target.value, 10))}
                        >
                          <option value="" disabled selected>
                            Select
                          </option>
                          {leadTypes.map((item: LeadType, i: number) => (
                            <option key={i} value={item.id}>
                              {item.lead_type}
                            </option>
                          ))}
                        </Form.Select>
                        {typeError && (
                          <p
                            role="alert"
                            className="text-danger fs-9 pt-1 mb-0"
                          >
                            Lead Type is Required
                          </p>
                        )}
                      </Form.Group>
                    </Col>

                    <Col sm={6} md={4}>
                      <Form.Group>
                        <Form.Label>
                          Status
                          <span className="text-danger"> *</span>
                        </Form.Label>
                        <Form.Select
                          className="form-select"
                          onChange={(e) => handleSelectStatus(parseInt(e.target.value, 10))}
                        >
                          <option value="" disabled selected>
                            Select
                          </option>
                          {selectedLeadStatus.map((item: LeadStatus, i: number) => (
                            <option key={i} value={item.id}>
                              {item.status}
                            </option>
                          ))}
                        </Form.Select>
                        {statusError && (
                          <p
                            role="alert"
                            className="text-danger fs-9 pt-1 mb-0"
                          >
                            Status is Required
                          </p>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                <div className="mb-6">
                  <h4 className="mb-3">Address</h4>
                  <Row className="g-3">
                    <Col sm={6} md={3}>
                      <Form.Group>
                        <Form.Label>Street Name</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Street Name"
                          {...register("street_name")}
                        />
                      </Form.Group>
                    </Col>

                    <Col sm={6} md={3}>
                      <Form.Group>
                        <Form.Label>City</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="City"
                          {...register("city")}
                        />
                      </Form.Group>
                    </Col>
                    <Col sm={6} md={3}>
                      <Form.Group>
                        <Form.Label>State</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="State"
                          {...register("state")}
                        />
                      </Form.Group>
                    </Col>
                    <Col sm={3}>
                      <Form.Group>
                        <Form.Label>Zip code</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Zip code"
                          {...register("zip_code")}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                <h4 className="mb-3">Description</h4>

                <Form.Group>
                  <Form.Label>Lead Profile Summary</Form.Label>
                  <Form.Control
                    as="textarea"
                    style={{ height: "128px" }}
                    type="text"
                    placeholder="Add Summary"
                    {...register("description")}
                  />
                </Form.Group>

                <Card className="mt-6">
                  <Card.Body>
                    <Form.Label className="form-label-header mb-2 fs-6">
                      Tags
                    </Form.Label>

                    <div className="border p-3 rounded-2 d-flex align-items-end gap-3 mb-4">
                      <div className="w-75">
                        <Form.Group>
                          <Form.Label>Tag</Form.Label>
                          <Form.Control
                            value={tags}
                            type="text"
                            placeholder="Add Tag"
                            onChange={(e) => setTags(e.target.value)}
                          />
                        </Form.Group>
                      </div>

                      <div className="w-25">
                        <Button
                          variant="outline-primary"
                          onClick={handleTags}
                        >
                          Add
                        </Button>
                      </div>
                    </div>

                    {storeTags?.map((item: Tag, i: number) => (
                      <div
                        className="mt-3 d-flex gap-6 align-items-center"
                        key={i}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <h4 className="mb-0"> #{item.tags}</h4>
                        </div>

                        <Button
                          variant="phoenix-danger"
                          size="sm"
                          className="py-1"
                          onClick={() => handleDeleteTag(item.tags)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </div>
                    ))}
                  </Card.Body>
                </Card>

                <div className="text-end">
                  <Button
                    variant="primary"
                    type="submit"
                    className="mt-6"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Creating...
                      </>
                    ) : (
                      "Create lead"
                    )}
                  </Button>
                </div>
              </Form>
            </Col>
          </Row>
        </>
      )}
      {!isAuthorized && <Backdrop />} 
    </div>
  );
};

export default AddLead;
